import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCreditNotes } from "@/api/queries";
import type { CreditNoteSummary } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import { DocumentStatusBadge } from "@/components/list/StatusBadge";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

export function CreditNotesListPage() {
  const search = useSearch({ from: "/sales/credit-notes" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useCreditNotes(listQueryFor(search));

  const columns: Array<Column<CreditNoteSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "12ch",
      numeric: true,
      render: (row) => row.number ?? "Draft",
    },
    { key: "customer", header: "Customer", render: (row) => row.customerName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "applied",
      header: "Applied to",
      width: "14ch",
      numeric: true,
      // A credit note that credits nothing in particular is legitimate but unusual, and saying so
      // is more useful than an empty cell somebody has to interpret.
      render: (row) => row.appliedTo ?? <span className="text-fg-hint">Not applied</span>,
    },
    { key: "reason", header: "Reason", render: (row) => row.reason },
    {
      key: "status",
      header: "Status",
      width: "14ch",
      render: (row) => <DocumentStatusBadge status={row.status} />,
    },
    {
      key: "total",
      header: "Amount",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.total),
    },
  ];

  return (
    <ListScreen
      route="/sales/credit-notes"
      title="Credit notes"
      subtitle={data ? `${data.total} this fiscal year` : "Loading credit notes"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="sales.creditnotes.create"
          onClick={() => void navigate({ to: "/sales/credit-notes/new" })}
        >
          New credit note
        </PermittedAction>
      }
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Credit note ${row.number ?? "draft"}, ${row.customerName}, ${formatAmount(row.total)}`
      }
      onRowClick={(row) => void navigate({ to: "/sales/credit-notes/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, customer or invoice"
      empty={{
        icon: "FileMinus",
        title: "No credit notes yet",
        description:
          "A credit note corrects an invoice that has already been issued. Issued documents are never edited.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No credit notes match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
